import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { combineLatest, fromEvent, Observable, Subscription, timer } from 'rxjs';
import { startWith } from 'rxjs/operators';
import * as d3 from 'd3';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { Days, TimeSlot } from '../../../store/entities';
import { compareTimeArrays, getIntervalBetweenTimeArrays } from '../../../utils';

const RADIANS = Math.PI / 180;
const HOUR_RADIANS = 360 / 24 * RADIANS;
const MINUTE_RADIANS = 360 / 24 / 60 * RADIANS;
const DEFAULT_COLORS = d3.scaleOrdinal(d3.schemeCategory10);

@Component({
  selector: 'mwo-setlist-clock',
  templateUrl: './setlist-clock.component.html',
  styleUrls: ['./setlist-clock.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  animations: [
    trigger('fadeInOut', [
      state('void', style({
        opacity: 0
      })),
      transition('void => *', animate(500)),
    ])
  ]
})
export class SetlistClockComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('clockPie', { static: true }) clockPieRef: ElementRef;
  @Input() slots$: Observable<Array<TimeSlot>>;
  @Input() currentSlot$: Observable<TimeSlot>;
  @Input() isAnimated$: Observable<boolean>;
  @Input() showEqualizer$: Observable<boolean>;
  @Input() isCurrentDay$: Observable<boolean>;
  @Input() day: Days;
  @Input() context$: Observable<string>;

  private timer: Subscription;

  private radius: number;
  private bandwidth: number;
  private svgContainer: d3.Selection<SVGGElement, TimeSlot, HTMLElement, any>;
  slotName: string;
  nameSlot: string;

  constructor() {
  }

  ngOnInit() {
    this.currentSlot$.subscribe(res => {
      this.slotName = '';
      if (res && res.event) {
        this.slotName = res.event.name;
      } else if (res && res.tempo) {
        this.slotName = res.tempo.name;
      } else if (res && res.name) {
        this.slotName = res.name;
      }
    });
  }

  ngAfterViewInit() {
    // eslint-disable-next-line import/no-deprecated
    const resizeEvent$ = fromEvent(window, 'resize').pipe(startWith(null));
    this.timer = this.initClock(this.clockPieRef, { event: { color: 'black' } });
    let timeStamp;
    let currentSlotId;
    combineLatest([resizeEvent$, this.slots$, this.currentSlot$]).subscribe(([resizeEvent, slots, currentSlot]) => {
      if (resizeEvent && resizeEvent.timeStamp !== timeStamp) {
        this.timer = this.initClock(this.clockPieRef, currentSlot);
        timeStamp = resizeEvent.timeStamp;
      } else if (currentSlotId && currentSlot?.id !== currentSlotId) {
        this.timer = this.initClock(this.clockPieRef, currentSlot);
        currentSlotId = currentSlot?.id;
      }
      this.buildPie(slots);
    });
  }

  ngOnDestroy() {
    if (this.timer) {
      this.timer.unsubscribe();
    }
  }

  /** D3 custom methods for this component */

  private initClock(el: ElementRef, currentSlot: TimeSlot): Subscription {
    if (!el) { return; }
    const host = d3.select<HTMLElement, TimeSlot>(el.nativeElement);
    const { size } = el.nativeElement.getBoundingClientRect();
    this.svgContainer = this.getSVG(host, { size });
    this.svgContainer.append('g').attr('class', 'doughnut');
    // this.svgContainer.append('g').attr('class', 'clock-face');
    // this.buildClock();

    return timer(0, 60000).subscribe(() => {
      const radius = this.radius - this.bandwidth - 16;
      const now = new Date();
      const time = now.getHours() * 60 + now.getMinutes();
      const color = currentSlot?.tempo ? currentSlot.tempo.color : currentSlot?.event?.color || 'black';
      this.buildTimerHand('hand-shadow', 'white', radius, time, 50, 3);
      this.buildTimerHand('hand', color, radius, time, 50);
    });
  }

  private buildPie(data: TimeSlot[]): void {
    if (data && data[0].event) {
      this.nameSlot = data[0].event.name;
    } else if (data && data[0].tempo) {
      this.nameSlot = data[0].tempo.name;
    }
    this.svgContainer.selectAll('.arc').remove();
    const arc = d3.arc<d3.PieArcDatum<TimeSlot>>()
      .innerRadius(this.radius - this.bandwidth)
      .outerRadius(this.radius)
      .padAngle(0.01)
      .cornerRadius(4);

    const values = d3.pie<any, TimeSlot>()
      .sort((a, b) => compareTimeArrays(a.startTime, b.startTime) ? 1 : -1)
      .value(parseValue)
      (data);

    const doughnut = this.svgContainer.selectAll('.doughnut');
    const CUSTOM_COLORS = ["#F0CE0E", "#FF5353", "#67397B", "#AB5B9C", "#4042AB", "#86A873", "095256"];
    doughnut
      .selectAll('.arc')
      .data(values)
      .enter()
      .append('g')
      .attr('class', 'arc')
      .append('path')
      .attr('d', arc)
      .attr('fill', (d, i) => {
        return getSlotColor(d.data) || CUSTOM_COLORS[i % CUSTOM_COLORS.length]
      }
      );
  }

  private buildClock(): void {
    this.svgContainer.selectAll('.face-dots').remove();
    const dotRadius = this.radius - this.bandwidth - 16;
    const labelRadius = dotRadius - 24;

    const clock = this.svgContainer.selectAll('.clock-face');
    const dots = clock.selectAll('.face-dots')
      .data(d3.range(1, 25)).enter()
      .append('g')
      .attr('class', 'face-dots')
      ;
    dots.append('circle')
      .attr('r', d => d % 3 ? 3 : 6)
      .attr('fill', '#4d4d4d')
      .attr('cx', placeX(dotRadius)())
      .attr('cy', placeY(dotRadius)())
      ;
    dots.append('text')
      .attr('font-family', 'Arial')
      .attr('font-size', 13)
      .attr('text-align', 'center')
      .attr('text-anchor', 'darkgrey')
      .attr('fill', '#4d4d4d')
      .attr('x', placeX(labelRadius, -6)())
      .attr('y', placeY(labelRadius, 3)())
      .text(d => d % 3 ? '' : d)
      ;
  }

  private buildTimerHand(idName: string, color: string, radius: number, data: number, lineWidth: number, translateY: number = 0) {
    this.svgContainer.selectAll(`.time-indicator-${idName}`).remove();
    this.svgContainer.selectAll(`.time-indicator-${idName}`)
      .data([data])
      .enter()
      .append('line')
      .attr('class', `time-indicator-${idName}`)
      .attr('id', `timer-${idName}`)
      .attr('stroke-width', lineWidth / 15)
      .attr('stroke', color)
      .attr('transform', () => `translate(0 ${translateY})`)
      .attr('x1', placeX(radius)(MINUTE_RADIANS))
      .attr('y1', placeY(radius)(MINUTE_RADIANS))
      .attr('x2', placeX(radius + lineWidth)(MINUTE_RADIANS))
      .attr('y2', placeY(radius + lineWidth)(MINUTE_RADIANS));
  }

  private getSVG(
    host: d3.Selection<HTMLElement, TimeSlot, HTMLElement, any>,
    { size = 400 }
  ): d3.Selection<SVGGElement, TimeSlot, HTMLElement, any> {
    const minValue = Math.min(size);
    const clockPadding = 40;
    this.radius = minValue / 2;
    this.bandwidth = minValue * 0.04;

    return host.html('')
      .append('svg')
      .attr('viewBox', `0 0 ${size + clockPadding} ${size + clockPadding}`)
      .append('g')
      .attr('transform', `translate(${(size + clockPadding) / 2}, ${(size + clockPadding) / 2})`);
  }
}

function parseValue(slot: TimeSlot): number {
  const { endTime, startTime } = slot;
  return getIntervalBetweenTimeArrays(startTime, endTime)
    .toDuration('seconds')
    .seconds;
}

function placeX(radius, shiftPosVal = 0) {
  return (rad = HOUR_RADIANS) => d => radius * Math.sin(d * rad) + shiftPosVal;
}
function placeY(radius, shiftPosVal = 0) {
  return (rad = HOUR_RADIANS) => d => -radius * Math.cos((d * rad)) + shiftPosVal;
}

function getSlotColor({ event, tempo }: TimeSlot): string {
  if (event && event.color) {
    return event.color;
  } else if (tempo && tempo.color) {
    return tempo.color;
  }
  return '';
}
